/**
 * How long a message typed into Franklin's chat may be, and the errors a send
 * can be refused with before anything reaches the thread.
 *
 * The chatbot backend (`ChatRequest.message`) has no length limit of its own,
 * so this is purely a front-end guard. It measures what the USER typed — the
 * attachment headers `useChatSend` prepends are ours, and must never eat into
 * the user's allowance.
 */

/** Longest message, in characters (UTF-16 code units, i.e. `String.length`). */
export const MAX_CHAT_MESSAGE_LENGTH = 20_000;

/** The composer starts showing a character counter from this length (80 %). */
export const CHAT_MESSAGE_COUNTER_THRESHOLD = Math.floor(MAX_CHAT_MESSAGE_LENGTH * 0.8);

/**
 * A send refused before the store touched the thread: no optimistic bubble, no
 * error bubble, nothing cleared. Whoever called the send still owns the text
 * and must hand it back to the user and say why — the thread will not.
 */
export class ChatSendRejectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ChatSendRejectedError";
  }
}

/** The typed text is longer than {@link MAX_CHAT_MESSAGE_LENGTH}. */
export class ChatMessageTooLongError extends ChatSendRejectedError {
  readonly length: number;
  readonly limit: number;

  constructor(length: number, limit: number = MAX_CHAT_MESSAGE_LENGTH) {
    super(`Message too long. Maximum length is ${limit} characters.`);
    this.name = "ChatMessageTooLongError";
    this.length = length;
    this.limit = limit;
  }
}

/** Length of the text as the limit counts it — surrounding whitespace is never sent. */
export function chatMessageLength(text: string): number {
  return text.trim().length;
}

export function isChatMessageTooLong(text: string): boolean {
  return chatMessageLength(text) > MAX_CHAT_MESSAGE_LENGTH;
}

/**
 * Throw the rejection a send of `userText` would meet, if any. Both store
 * entry points (`submitMessage`, `sendMessage`) call this before they add
 * anything to the thread.
 */
export function assertChatMessageSendable(userText: string): void {
  const length = chatMessageLength(userText);
  if (length === 0) throw new ChatSendRejectedError("Message cannot be empty");
  if (length > MAX_CHAT_MESSAGE_LENGTH) throw new ChatMessageTooLongError(length);
}
